import { useState } from "react";
import { useTranslation } from "react-i18next";
import PlagiarismOutlinedIcon from "@mui/icons-material/PlagiarismOutlined";
import { Button, Stack } from "~/common/mui";
import Modal, { ModalSize } from "~/common/components/Modal";
import { LinkButton } from "~/common/components/Link";
import { useBooleanState } from "../../../../../common/hooks/useBooleanState";
import PriceReferenceDropdown from "~/features/productDetails/components/AddToCartCard/PriceReferenceDropdown";
import { userCanSeeEndCustomer } from "../LineItemStaticParams";
import { userIsDistributor, userIsPurchaser } from "~/domains/user/utils/roles";
import { QuickActionMenuItem } from "./CartQuickActionsButton";

interface Props {
  className?: string;
  updatePriceReference: (priceReference: string) => void;
  isB2BCart?: boolean;
}

export const ChoosePriceReferenceForCart = ({ className, isB2BCart = false, updatePriceReference }: Props) => {
  const { t } = useTranslation();

  const { val: modalOpen, setTrue: open, setFalse: close } = useBooleanState(false);
  const [priceReference, setPriceReferece] = useState("");

  if (!window.SCHA.feature.showChoosePriceRefForCartButton) {
    return null;
  }

  if (userCanSeeEndCustomer()) {
    return null;
  }

  const isDistributorOrPurchaser =
    (userIsDistributor() || userIsPurchaser()) && window.SCHA.config.priceReferences.length >= 2;
  if (!isDistributorOrPurchaser) {
    return null;
  }

  const onSave = () => {
    close();
    updatePriceReference(priceReference);
  };

  const buttonText = t(
    isB2BCart ? "cartPage.btn.choosePriceReferenceForCart" : "cartPage.bulkUpload.btn.selectPriceReferenceForAll",
  );

  const choosePriceReferenceBtn = (
    <LinkButton
      iconBefore={<PlagiarismOutlinedIcon sx={{ fontSize: 20 }} />}
      onClick={() => open()}
      data-test-id={"btn-load-all-pna"}
      className={className}
    >
      {buttonText}
    </LinkButton>
  );

  const choosePriceReferenceBtnForBulkPopup = (
    <Button variant="text" startIcon={<PlagiarismOutlinedIcon />} onClick={() => open()}>
      {buttonText}
    </Button>
  );

  return (
    <>
      {isB2BCart ? (
        <QuickActionMenuItem>{choosePriceReferenceBtn}</QuickActionMenuItem>
      ) : (
        choosePriceReferenceBtnForBulkPopup
      )}

      {modalOpen && (
        <Modal
          size={ModalSize.SMALL}
          onClose={close}
          hideClose={true}
          title={t(
            isB2BCart
              ? "cartPage.modal.choosePriceReferenceForCart.title"
              : "cartPage.modal.choosePriceReferenceForBulkEntry.title",
          )}
          footerChildren={
            <Stack gap={1} direction={"row"}>
              <Button variant={"text"} onClick={close}>
                {t("cancel")}
              </Button>
              <Button color="primary" onClick={onSave}>
                {t("text.company.save.button")}
              </Button>
            </Stack>
          }
        >
          <PriceReferenceDropdown
            hideLabel={true}
            selected={priceReference}
            onChange={(val) => setPriceReferece(val as string)}
          />
        </Modal>
      )}
    </>
  );
};
